import * as constants from "./constants";
import history from "utils/history";
import { actionCreators as actionCreatorsModal } from "common/modal";
import { actionCreators as actionCreatorsUser } from "../../manageaccount/store";
import { actionCreators as actionCreatorsToast } from "common/toastNotification/store";
import { actionCreators as actionCreatorsHeader } from "common/header/store";
import { deleteUploadingFile } from "common/claim_cert_attachment/store/actionCreators";
import API from "../../../server";
import {
  downloadFile as downloadLink,
  viewPDF,
} from "utils/createDownloadLink";
import { format } from "date-fns";
import { ClaimContract } from "../components/pageMenu";
import {
  convertTimeToUTC,
  compareDates,
  parseFloatValue,
  floorValue,
  roundNumber,
} from "utils/helpers";
import { STATUS_DRAFT, STATUS_REVIEW } from "utils/constants";
import {
  addValues,
  divideValues,
  itemQuantity,
  multiplyValues,
  roundNearestInt,
} from "utils/helpers/math";

const getApprovalList = (proId, contractId, claimId) => {
  return (dispatch) => {
    API.read_claim_workflow(proId, contractId, claimId)
      .then((res) => res.data)
      .then((ele) => {
        dispatch(setTableData("approvalList", ele.data.data));
      });
  };
};

const sendContractOverviewInfo = (contractInfo, period, pre_claim_name) => ({
  type: constants.REQUEST_CONTRACT_INFO,
  contractInfo,
  period,
  pre_claim_name,
});
const setInitialClaimInfo = (
  claimInfo,
  pre_claim_name,
  third_party_info,
  contractType,
) => ({
  type: constants.REQUEST_CLAIM_INFO,
  claimInfo,
  pre_claim_name,
  third_party_info,
  contractType,
});

//to read a claim (pdf version), previously requestProjectInfo()
export const requestContractInfo = (
  proId,
  contractId,
  claimId,
  contractType,
  callback,
) => {
  /*
   * if the route has claimid
   * go to read a claim and set up info
   * if it is new and to create a new claim
   * go to  empty fields and set up environment
   */
  return async (dispatch) => {
    // to get the previous latest claim reference
    const contractSummary = await API.read_contract_summary(
      proId,
      contractId,
    ).then((res) => res.data.data.contractsummary);
    let preClaimName = "";
    const arr = ["approved", "issue", "certified"];

    if (contractSummary) {
      const obj = contractSummary.find((item) =>
        arr.includes(item.claim_status),
      );
      preClaimName = obj?.claim_name || "";
    }

    if (!claimId) {
      const claimPeriod = await API.read_contract_claim_period(
        proId,
        contractId,
      ).then((res) => res.data.data.data);
      dispatch(getClaimNetCert(proId, contractId, claimPeriod.claim_to));
      const contractRes = await API.read_contract(proId, contractId).then(
        (res) => res.data.data.data,
      );
      dispatch(
        sendContractOverviewInfo(contractRes, claimPeriod, preClaimName),
      );
      return;
    }

    const readClaim = await API.read_one_claim(proId, contractId, claimId).then(
      (res) => res.data.data.readpayclaim,
    );
    const claimToDate = readClaim.claim_to;

    // New Claim Cert Summary Component
    dispatch(setPreviousClaimCert(contractSummary, claimId, contractType));

    if (contractType === "cert") {
      dispatch(
        setInitialClaimInfo(
          readClaim,
          preClaimName,
          { to_contacts: "" },
          contractType,
        ),
      );
      dispatch(getClaimNetCert(proId, contractId, claimToDate));
      return;
    }

    const thirdPartyInfo = await API.read_claim_notify(
      proId,
      contractId,
      claimId,
    ).then((res) => res.data.data.contacts);
    callback?.({ previous_option: readClaim?.previous?.option });

    //overwrite previous_claim_name base on select claim to_date
    if (contractSummary) {
      const obj = contractSummary.find(
        (item) =>
          arr.includes(item.claim_status) &&
          compareDates(item.period_ending, claimToDate) !== "greater",
      );
      preClaimName = obj?.claim_name || "";
    }

    dispatch(
      setInitialClaimInfo(
        readClaim,
        preClaimName,
        thirdPartyInfo,
        contractType,
      ),
    );
    dispatch(actionCreatorsUser.getAllUsers());
    dispatch(getApprovalList(proId, contractId, claimId));
    dispatch(getClaimNetCert(proId, contractId, claimToDate));
    if (readClaim.has_retention) {
      // check whether claim retention
      // only when pcd/dlp value is not equal to previous cert release value
      // mark it release claim retention
      const cert = await API.read_claim_net_cert(
        proId,
        contractId,
        claimToDate,
      ).then((res) => res.data.data);
      if (
        readClaim.dlp_release_value !== cert.value.cert_dlp ||
        readClaim.pcd_release_value !== cert.value.cert_pcd
      ) {
        dispatch(setData("toClaimRetention", true));
        //new Summary Values
        dispatch(setData("claimDlpReleaseValue", readClaim.dlp_release_value));
        dispatch(setData("claimPcdReleaseValue", readClaim.pcd_release_value));
      }
    }
  };
};

export const updateSingleClaimVariationsItem = (
  proId,
  contractId,
  claimId,
  contractType,
  lineId,
  extraInfo,
  callbackFunction,
) => {
  return (dispatch) => {
    API.update_one_lineitem(proId, contractId, lineId, extraInfo).then(
      (res) => {
        API.read_one_claim(proId, contractId, claimId)
          .then((res) => res.data.data)
          .then((res) => res.readpayclaim)
          .then((res) => {
            const lineItemDetails = res?.variation_items.find(
              (item) => item.id === lineId,
            );
            if (lineItemDetails) {
              dispatch(
                updateVariationLineItemsInRedux(lineId, {
                  res,
                  lineItemDetails,
                }),
              );

              if (callbackFunction) {
                callbackFunction(lineItemDetails);
              }
            }
          });
      },
    );
  };
};

export const readContract = (proId, contractId, callback) => {
  return (dispatch) => {
    API.read_contract(proId, contractId)
      .then((res) => res.data.data.data)
      .then((res) => {
        dispatch(
          setData(
            "certifyPendingVariation",
            res.setting.certify_pending_variation,
          ),
        );
        dispatch(
          setData(
            "claimBulkSending",
            res?.setting?.email_setting?.claim?.is_bulk ?? false,
          ),
        );
        dispatch({
          type: constants.SET_CONTRACT_DETAIL,
          payload: res,
        });
        callback?.();
      });
  };
};

// delete a claim
export const deleteClaim = (proId, contractId, claimId, contractType) => {
  return (dispatch, getState) => {
    // return to project overview page (or should it be contract overview page?) after deleting claim
    const accountId = getState().getIn([
      "headers",
      "profile",
      "payclaim_account_id",
    ]);
    let contractPageType = ClaimContract.includes(contractType)
      ? "claim"
      : "cert";
    let return_url = `/contract/${proId}/${contractPageType}/${contractId}?account_id=${accountId}`;
    if (claimId) {
      dispatch(
        actionCreatorsModal.showModal("delete", {
          open: true,
          title: "Claim",
          message: "claim",
          url: return_url,
          api: {
            name: "delete_one_claim",
            config: [proId, contractId, claimId],
          },
        }),
      );
    } else {
      history.push(return_url);
    }
  };
};

export const setInputValue = (
  id,
  field,
  value,
  name,
  loadVal,
  fromPrctQty,
) => ({
  type: constants.SET_INPUT_VALUE,
  id,
  field,
  value,
  name,
  fromLoad: loadVal,
  fromPrctQty: fromPrctQty,
});

export const setMaterialInput = (lineId, value, onSite, itemKey = "value") => ({
  type: constants.SET_MATERIAL_INPUT,
  lineId,
  value,
  key: itemKey,
  onSite,
});

export const setValue = (name, value) => ({
  type: constants.SET_NAME_VALUE,
  name: name,
  value: value,
});

export const setClaimInfo = (
  name,
  value,
  proId,
  contractId,
  claimId = "",
  integration = "",
  contractType,
) => {
  return (dispatch, getState) => {
    dispatch({
      type: constants.SET_CLAIM_DATES,
      name,
      value,
    });
    if (contractId) {
      let claimFrom = getState().getIn([
          "makeaclaim",
          "claimDetail",
          "claim_from",
        ]),
        claimTo = getState().getIn(["makeaclaim", "claimDetail", "claim_to"]),
        params = new URLSearchParams(),
        date = format(new Date(value), "yyyy-MM-dd");

      claimFrom = format(new Date(claimFrom), "yyyy-MM-dd");
      claimTo = format(new Date(claimTo), "yyyy-MM-dd");

      if (name === "claim_from") {
        params.append("claim_from", date);
      }
      if (name === "claim_to") {
        params.append("claim_from", claimFrom);
        params.append("claim_to", date);
      }
      if (name === "claim_due_date") {
        params.append("claim_from", claimFrom);
        params.append("claim_to", claimTo);
        params.append("claim_due", date);
      }

      API.read_contract_claim_period(proId, contractId, params)
        .then((res) => res.data.data.data)
        .then((claimPeriod) => {
          dispatch(setClaimPeriod(claimPeriod));
          dispatch(getClaimNetCert(proId, contractId, claimPeriod.claim_to));
          //update previous option if claim_to date has changed. the previous claim/cert also need to be updated.
          dispatch(
            getPreviousClaimCert(
              proId,
              contractId,
              claimId,
              contractType,
              claimPeriod.claim_to,
            ),
          );
          if (
            integration === "ipm" &&
            (name === "claim_from" || name === "claim_to")
          ) {
            dispatch(
              saveDateChange(
                proId,
                contractId,
                claimId,
                claimPeriod,
                contractType,
              ),
            );
          }
        });
    }
  };
};

export const saveDateChange = (
  proId,
  contractId,
  claimId,
  claimDetail,
  contractType,
) => {
  return (dispatch) => {
    // rename to fit claim struct
    let dataSet = {
      from_date: claimDetail.claim_from,
      to_date: claimDetail.claim_to,
      payment_due_date: claimDetail.pay_due_date,
      cert_due_date: claimDetail.cert_due_date,
      claim_due_date: claimDetail.claim_due_date,
    };

    API.update_claim_info(proId, contractId, claimId, dataSet).then(() => {
      // reload claim details to update vari items
      dispatch(readMakeClaimDetails(proId, contractId, claimId, contractType));
    });
  };
};

const setClaimPeriod = (claimPeriod) => ({
  type: constants.UPDATE_CLAIM_DATES,
  claimPeriod,
});

// to make a claim (draft)
export const makeAclaim = (proId, contractId, contractType, field) => {
  return (dispatch, getState) => {
    const accountId = getState().getIn([
      "headers",
      "profile",
      "payclaim_account_id",
    ]);
    API.create_one_claim(proId, contractId)
      .then((res) => res.data)
      .then((res) => res.data.makepayclaim.id)
      .then((res) => {
        let nextPath = `/project/${proId}/contract/${contractId}/${contractType}/claim/${res}/review?account_id=${accountId}`;
        if (field === "claim") {
          history.push(nextPath);
        } else {
          history.replace(
            `/project/${proId}/contract/${contractId}/${contractType}/claim/${res}?account_id=${accountId}`,
          );
        }

        // New Claim Cert Summary Component
        dispatch(getPreviousClaimCert(proId, contractId, res));
      });
  };
};
//download the pdf
export const downloadPdf = (proId, contractId, claimId, isDownload) => () => {
  return API.download_claim_pdf(proId, contractId, claimId, {
    responseType: "blob",
  }).then((res) => {
    if (isDownload) {
      downloadLink(res);
    } else {
      viewPDF(res);
    }
  });
};

export const resetStage = () => ({
  type: constants.RESET_STAGE,
});

export const changeClaimToApproved = (proId, contractId, claimId) => {
  return (dispatch, getState) => {
    const claimApprovedMessage = getState().getIn([
      "config",
      "accountConfig",
      "pages",
      "claim",
      "claim_toast_terms",
      "toast_approved",
    ]);

    //create xero invoice if settings match
    const xeroIntegrationType = getState().getIn([
      "manageAccount",
      "xeroSettings",
      "settings",
      "invoice_generation_position",
    ]);

    const xeroInvoiceLinks = getState().getIn([
      "makeaclaim",
      "contractInfo",
      "xero_invoice_links",
    ]);

    const automaticXeroInvoiceGeneration =
      getState().getIn([
        "manageAccount",
        "xeroSettings",
        "settings",
        "generate_invoice",
      ]) === "auto";

    // only automatically generate an invoice if one does not already exist
    const canAutoGenerateXeroInvoice =
      automaticXeroInvoiceGeneration && xeroInvoiceLinks.size === 0;

    API.approve_one_claim(proId, contractId, claimId).then(async () => {
      if (xeroIntegrationType === "after-claim" && canAutoGenerateXeroInvoice) {
        const invoice = await API.create_xero_invoice(
          proId,
          contractId,
          claimId,
        ).then((res) => {
          return res.data.data.data;
        });
        await API.link_claim_xero_invoice(
          proId,
          contractId,
          claimId,
          invoice.invoice_id,
        );
      }
      dispatch(setValue("approvingClaim", false));
      dispatch(requestContractInfo(proId, contractId, claimId));
      dispatch(
        actionCreatorsToast.addToast(claimId, {
          type: "information",
          title: claimApprovedMessage,
        }),
      );
    });
  };
};

//approve a claim
export const approveClaim = (proId, contractId, claimId) => {
  return (dispatch, getState) => {
    let approveList = getState().getIn(["makeaclaim", "approvalList"]);
    if (
      approveList.get("current_step_index") >=
      approveList.get("steps").size - 1
    ) {
      dispatch(setValue("approvingClaim", true));
      dispatch(changeClaimToApproved(proId, contractId, claimId));
    } else {
      dispatch(setValue("leaveComment", true));
    }
  };
};

export const approveClaimByStep = (proId, contractId, claimId) => {
  return (dispatch, getState) => {
    dispatch(setValue("approvingClaim", true));
    let approveList = getState().getIn(["makeaclaim", "approvalList"]),
      comment = {
        comment: getState().getIn(["makeaclaim", "approval_comment"]),
      };

    let currentStep = approveList
      .get("steps")
      .find(
        (item) => item.get("step") === approveList.get("current_step_index"),
      );
    if (currentStep) {
      API.approve_claim_step(
        proId,
        contractId,
        claimId,
        currentStep.get("id"),
        comment,
      )
        .then((res) => res.data)
        .then((res) => {
          API.read_claim_workflow(proId, contractId, claimId)
            .then((res) => res.data)
            .then((res) => {
              dispatch(closeDialog());
              dispatch(setTableData("approvalList", res.data.data));
              if (
                res.data.data.current_step_index >=
                res.data.data.steps.length - 1
              ) {
                dispatch(changeClaimToApproved(proId, contractId, claimId));
              } else {
                dispatch(setValue("approvingClaim", false));
              }
            });
        });
    }
  };
};

export const revertByStep = (
  proId,
  contractId,
  claimId,
  stepId,
  comment,
  nextStatus = STATUS_REVIEW,
) => {
  return async (dispatch) => {
    const body = { comment };
    await API.revert_claim_workflow(proId, contractId, claimId, stepId, body);
    dispatch(getApprovalList(proId, contractId, claimId));
    dispatch(setValue("claim_status", nextStatus));
  };
};

export const sendClaimForApproval = (proId, contractId, claimId, body) => {
  return async (dispatch) => {
    await API.send_approval_one_claim(proId, contractId, claimId, body);
    dispatch(getApprovalList(proId, contractId, claimId));
    dispatch(setValue("claim_status", STATUS_REVIEW));
  };
};

export const revertClaimForApproval = (proId, contractId, claimId, body) => {
  return async (dispatch) => {
    await API.revert_claim_to_review(proId, contractId, claimId, body);
    dispatch(getApprovalList(proId, contractId, claimId));
    dispatch(setValue("claim_status", STATUS_REVIEW));
  };
};

export const closeDialog = () => {
  return (dispatch) => {
    dispatch(setValue("approval_comment", ""));
    dispatch(setValue("leaveComment", false));
  };
};

//issue a claim
export const issueClaim = (
  proId,
  contractId,
  claimId,
  isSendEmail,
  emailToList,
  subject,
  attachedEmailFiles,
) => {
  let attachments = attachedEmailFiles.filter((item) =>
    item.get("is_attached_email"),
  );
  let contacts = {
    to_contacts: emailToList.toJS(),
    subject: subject,
    attachments: attachments.toJS(),
    has_email_service: isSendEmail,
  };

  return (dispatch, getState) => {
    if (isSendEmail && contacts.to_contacts.length < 1) {
      dispatch(
        actionCreatorsModal.showModal("alert", {
          open: true,
          title: "Error!",
          message: "Please input at least one email address!",
        }),
      );
    } else {
      dispatch(setValue("sendingEmail", true));
      let isPlural = contacts.to_contacts.length > 1;

      const claimToastTerms = getState().getIn([
        "config",
        "accountConfig",
        "pages",
        "claim",
        "claim_toast_terms",
      ]);

      const claimSettings =
        getState()
          ?.getIn(["makeaclaim", "contractInfo", "claim_settings"])
          ?.toObject() || {};
      const claimContractSettings =
        getState()
          ?.getIn(["makeaclaim", "contractInfo", "setting"])
          ?.toObject() || {};
      const enableCustomToast = claimSettings.enable_custom_toast ?? false;

      // Push to Third Party when maintenance mode is off and push to third party is on
      const pushToThirdParty =
        claimContractSettings?.maintenance_mode === false &&
        claimSettings.push_to_third_party === true
          ? true
          : false;
      const isSendBulk = getState()?.getIn(["makeaclaim", "claimBulkSending"]);
      contacts.is_bulk = isSendBulk;

      API.issue_one_claim(proId, contractId, claimId, contacts)
        .then((res) => res?.data)
        .then((res) => {
          if (!res) {
            dispatch(setValue("sendingEmail", false));
            return;
          }
          API.send_payclaim_email(proId, contractId, claimId, contacts)
            .then((res) => res.data)
            .then((res) => {
              dispatch(setValue("sendingEmail", false));
              dispatch(
                setData(
                  "componentKey",
                  Math.random().toString(36).substring(2),
                ),
              );

              dispatch(
                actionCreatorsToast.addToast(claimId, {
                  type: "success",
                  title: isPlural
                    ? claimToastTerms?.get("toast_issue_and_emails")
                    : claimToastTerms?.get("toast_issue_and_email"),
                }),
              );
            });

          if (pushToThirdParty) {
            API.issue_one_claim_integration(
              proId,
              contractId,
              claimId,
              contacts,
            )
              .then((res) => res.data)
              .then((res) => {
                if (res?.status === 201 && enableCustomToast) {
                  dispatch(
                    actionCreatorsToast.addToast(claimId, {
                      type: "information",
                      title: claimToastTerms?.get("toast_to_ipm"),
                    }),
                  );
                  dispatch(actionCreatorsHeader.getAccountIntegrationLogs());
                }
              })
              .catch((error) => {
                dispatch(actionCreatorsHeader.getAccountIntegrationLogs());
              });
          }

          // attach payclaim to xero invoice if setting is true
          const attachClaimToXero = getState().getIn([
            "manageAccount",
            "xeroSettings",
            "settings",
            "attach_claim",
          ]);
          const xeroInvoices =
            getState()
              .getIn(["makeaclaim", "contractInfo", "xero_invoice_links"])
              ?.toJS() || [];

          if (attachClaimToXero && xeroInvoices.length > 0) {
            xeroInvoices.forEach((invoice) => {
              API.attach_claim_to_invoice(
                proId,
                contractId,
                claimId,
                invoice.invoice_id,
              );
            });
          }
        });
    }
  };
};

export const removeEmail = (data, field) => ({
  type: constants.REMOVE_EMAIL,
  data,
  field,
});
export const setEmailSubject = (name, value) => ({
  type: constants.SET_EMAIL_SUBJECT,
  name: name,
  value: value,
});
export const addEmail = (data, field) => ({
  type: constants.ADD_EMAIL,
  data,
  field,
});
export const addContact_Email = (entityId, contactId, field) => {
  return (dispatch) => {
    API.read_company_contact(entityId, contactId)
      .then((res) => res.data)
      .then((res) => res.data.data)
      .then((res) => {
        dispatch(addEmail(res, field));
      });
  };
};

export const revertToDraft = (
  proId,
  contractId,
  claimId,
  contractType,
  comment,
  shouldRedirect = false,
  shouldGetWorkflow = false,
) => {
  return async (dispatch, getState) => {
    const accountId = getState().getIn([
      "headers",
      "profile",
      "payclaim_account_id",
    ]);
    // New Claim Cert Summary Component
    dispatch(getPreviousClaimCert(proId, contractId, claimId));
    const body = { comment };
    await API.revert_claim_to_draft(proId, contractId, claimId, body);

    if (shouldRedirect) {
      history.push(
        `/project/${proId}/contract/${contractId}/${contractType}/claim/${claimId}?account_id=${accountId}`,
      );
      dispatch(actionCreatorsHeader.getAccountIntegrationLogs());
    }
    if (shouldGetWorkflow) {
      dispatch(getApprovalList(proId, contractId, claimId));
    }
    dispatch(setValue("claim_status", STATUS_DRAFT));
  };
};

const setTableData = (name, data) => ({
  type: constants.SET_TABLE_DATA,
  name,
  data,
});

export const sendEmail = (
  proId,
  contractId,
  claimId,
  isSendEmail,
  emailToList,
  subject,
  attachedEmailFiles,
) => {
  let attachments = attachedEmailFiles.filter((item) =>
    item.get("is_attached_email"),
  );
  let contacts = {
    to_contacts: emailToList.toJS(),
    subject: subject,
    attachments: attachments.toJS(),
    has_email_service: isSendEmail,
  };
  return (dispatch, getState) => {
    dispatch(setValue("sendingEmail", true));
    let isPlural = contacts.to_contacts.length > 1;
    const isSendBulk = getState()?.getIn(["makeaclaim", "claimBulkSending"]);
    contacts.is_bulk = isSendBulk;

    API.send_payclaim_email(proId, contractId, claimId, contacts)
      .then((res) => res.data)
      .then((res) => {
        dispatch(
          actionCreatorsToast.addToast(claimId, {
            type: "information",
            title: `${isPlural ? "Emails" : "Email"} sent successfully`,
          }),
        );
        dispatch(setValue("sendingEmail", false));
        dispatch(getApprovalList(proId, contractId, claimId));
      });
  };
};

export const updateVariationLineItemsInRedux = (lineitemId, options = {}) => ({
  type: constants.UPDATE_VARIATION_LINE_ITEMS,
  lineitemId,
  ...options,
});

export const setVariationLineItemInRedux = (lineitemDetails, dbOperation) => ({
  type: constants.SET_NEW_VARIATION_LINE_ITEM,
  lineitemDetails,
  dbOperation,
});

export const updateComment = (
  proId,
  contractId,
  claimId,
  action,
  content,
  comment_id,
  lineId,
) => {
  let data = {
    content: content.comment,
    is_claim: true,
    doc_uuid: claimId,
    comment_id: comment_id,
    shared: content.shared,
  };

  let api;
  if (action === "post") {
    delete data.comment_id;
    api = API.create_lineitem_comment(proId, contractId, lineId, data);
  } else if (action === "delete") {
    api = API.delete_lineitem_comment(proId, contractId, lineId, data);
  } else {
    api = API.update_lineitem_comment(proId, contractId, lineId, data);
  }

  return (dispatch) => {
    api.then(() => {
      dispatch(readLineitemComments(proId, contractId, claimId, lineId));
    });
  };
};

// read comments (for specific lineitem) and make them the only state to update in redux
export const readLineitemComments = (proId, contractId, claimId, lineId) => {
  return (dispatch) => {
    API.read_one_claim(proId, contractId, claimId)
      .then((res) => res.data.data.readpayclaim)
      .then((res) => {
        dispatch(
          setLineitemComments(
            res?.baseitems,
            res?.variation_items,
            res?.materials,
            lineId,
          ),
        );
      });
  };
};

const setLineitemComments = (
  baseItems,
  variationItems,
  materialItems,
  lineId,
) => ({
  type: constants.SET_LINE_COMMENTS,
  baseItems,
  variationItems,
  materialItems,
  lineId,
});

export const deleteFile = (proId, contractId, claimId, fileId) => {
  return (dispatch) => {
    dispatch(
      actionCreatorsModal.showModal("delete", {
        open: true,
        title: "Files",
        message: "file",
        api: {
          name: "delete_file_claim",
          config: [proId, contractId, fileId],
        },
        action: [removeFile(fileId), deleteUploadingFile(fileId)],
      }),
    );
  };
};

const removeFile = (fileId) => ({
  type: constants.DELETE_FILE,
  payload: fileId,
});

//download File
export const downloadFile = (fileId, fileName, token, accId, isDownload) => {
  const openFunc = isDownload ? downloadLink : viewPDF;
  return () => {
    if (token || accId) {
      token
        ? API.download_one_shared_file_by_token(token, {
            responseType: "blob",
          }).then((res) => {
            openFunc(res);
          })
        : API.download_one_shared_file(
            `/api/payclaim/account/${accId}/file/${fileId}`,
            { responseType: "blob" },
          ).then((res) => {
            openFunc(res);
          });
    } else {
      API.download_one_file(fileId, { responseType: "blob" }).then((res) => {
        openFunc(res, fileName);
      });
    }
  };
};

export const changeSharedFiles = (
  proId,
  contractId,
  claimId,
  fileId,
  isAdd,
) => {
  return (dispatch) => {
    API.update_claim_shared_attachment(proId, contractId, claimId, {
      file_id: fileId,
      is_shared: isAdd,
    })
      .then((res) => res.data)
      .then((res) => {
        dispatch(requestContractInfo(proId, contractId, claimId));
      });
  };
};

export const selectAttachedFile = (name, value, field) => {
  return (dispatch, getState) => {
    const MAX_ATTACH_FILE_SIZE = getState().getIn(["headers", "config"])
      .file_size.file_upload_size_limit;
    let fileList = getState()
        .getIn(["makeaclaim", "attachedEmailFiles"])
        .toJS(),
      attachmentSize = 0;
    fileList.map((item) => {
      if (name === "all") {
        item[field] = value;
      } else {
        if (item.file_id === name) {
          item[field] = value;
        }
      }
      if (item.is_attached_email && item.attach_method === "file") {
        attachmentSize = addValues([attachmentSize, item.file_size]);
      }
      return item;
    });
    if (attachmentSize > MAX_ATTACH_FILE_SIZE) {
      dispatch(setData("exceedAttachLimit", true));
    } else {
      dispatch(setData("exceedAttachLimit", false));
    }
    dispatch(setTableData("attachedEmailFiles", fileList));
  };
};

export const getClaimNetCert = (proId, contractId, toDate) => {
  return (dispatch) => {
    // get previous cert info based on claim_to date
    API.read_claim_net_cert(proId, contractId, toDate)
      .then((res) => res.data.data)
      .then((cert) => {
        // based on previous cert retention to calculate max dlp/pcd
        API.calculate_retention(proId, contractId, {
          retention: Math.round(cert.value.cert_retention),
        })
          .then((res) => res.data)
          .then((res) => res.data.data)
          .then((res) => {
            dispatch(
              setData("certRetention", {
                dlp: divideValues([cert.value.cert_dlp, 100]),
                pcd: divideValues([cert.value.cert_pcd, 100]),
                preNetTotal: divideValues([cert.value.cert_value, 100]),
              }),
            );
          });
      });
  };
};

// New project and contract details (P2C)
export const setData = (name, value) => ({
  type: constants.SET_DATA,
  payload: {
    name,
    value,
  },
});

export const readProject = (proId) => {
  return (dispatch) => {
    API.read_one_project_group(proId)
      .then((res) => res.data.data)
      .then((res) => {
        dispatch(setData("projectInfo", res.data));
      });
  };
};

export const setAuditSetting = (proId, contractId, claimId, value) => {
  return (dispatch) => {
    API.update_claim_info(proId, contractId, claimId, {
      attach_audit_trail: value,
    })
      .then((res) => res.data)
      .then((res) => {
        dispatch(setData("isAuditAttached", value));
      });
  };
};

export const setContractConfig = (proId, contractId) => {
  return (dispatch) => {
    API.read_contract_config(proId, contractId)
      .then((res) => res.data)
      .then((res) => {
        dispatch(setData("contractConfig", res.data.data));
      });
  };
};

// read and update make claim details (for claims that already exist)
export const readMakeClaimDetails = (
  proId,
  contractId,
  claimId,
  contractType,
) => {
  return (dispatch, getState) => {
    API.read_contract_summary(proId, contractId)
      .then((res) => res.data.data.contractsummary)
      .then((contractsummary) => {
        let pre_claim_name = "";
        let arr = ["approved", "issue", "certified"];
        API.read_claim_notify(proId, contractId, claimId)
          .then((res) => res.data.data.contacts)
          .then((third_party_info) => {
            API.read_one_claim(proId, contractId, claimId)
              .then((res) => res.data.data.readpayclaim)
              .then((res) => {
                //overwrite previous_claim_name base on select claim to_date
                if (contractsummary) {
                  let obj = contractsummary.find(
                    (item) =>
                      arr.includes(item.claim_status) &&
                      compareDates(item.period_ending, res.claim_to) !==
                        "greater",
                  );
                  pre_claim_name = obj ? obj.claim_name : "";
                }
                const claimDetails = getState().getIn([
                  "makeaclaim",
                  "claimDetail",
                ]);
                res.payclaim_name = claimDetails?.get("claim_ref");
                res.claim_internal_notes = claimDetails?.get(
                  "claim_internal_notes",
                );
                res.claim_external_notes = claimDetails?.get(
                  "claim_external_notes",
                );
                dispatch(
                  setInitialClaimInfo(
                    res,
                    pre_claim_name,
                    third_party_info,
                    contractType,
                  ),
                );
                dispatch(getClaimNetCert(proId, contractId, res.claim_to));
                if (res.has_retention) {
                  let date = res.claim_to;
                  API.read_claim_net_cert(proId, contractId, date)
                    .then((res) => res.data.data)
                    .then((cert) => {
                      if (
                        res.dlp_release_value !== cert.value.cert_dlp ||
                        res.pcd_release_value !== cert.value.cert_pcd
                      ) {
                        dispatch(setData("toClaimRetention", true));
                        //new Summary Values
                        dispatch(
                          setData(
                            "claimDlpReleaseValue",
                            res.dlp_release_value,
                          ),
                        );
                        dispatch(
                          setData(
                            "claimPcdReleaseValue",
                            res.pcd_release_value,
                          ),
                        );
                      }
                    });
                }
              });
          });
        dispatch(setPreviousClaimCert(contractsummary, claimId, contractType));
      });
  };
};

// New Claim Cert Summary Actions

//New update a claim
export const updateAclaim = (
  claimDetail,
  lessPaid,
  baseList,
  variationList,
  proId,
  contractId,
  claimId,
  field,
  lessRetention,
  previousType,
  contractType,
  lessPreviousOption,
  showType = "modal",
  message,
) => {
  baseList = baseList.toJS();
  baseList = baseList.map((item) => {
    item.percent = parseFloatValue(item.percent);
    item.value = roundNearestInt(item.value);
    item.claim_qty = roundNumber(
      itemQuantity(item?.value, item?.total, item?.total_item_quantity),
    );
    return item;
  });
  variationList = variationList.toJS();
  variationList = variationList.map((item) => {
    item.percent = parseFloatValue(item.percent);
    item.value = roundNearestInt(item.value);
    item.claim_qty = roundNumber(
      itemQuantity(item?.value, item?.total, item?.total_item_quantity),
    );
    return item;
  });
  const timezone = claimDetail?.get("contract_timezone");

  let dataSet = {
    title: claimDetail.get("claim_ref"),
    from: convertTimeToUTC(
      claimDetail.get("claim_from") + " 00:00:00",
      timezone,
    ),
    to: convertTimeToUTC(claimDetail.get("claim_to") + " 23:59:59", timezone),
    last: convertTimeToUTC(
      claimDetail.get("claim_due_date") + " 23:59:59",
      timezone,
    ),
    due: convertTimeToUTC(
      claimDetail.get("pay_due_date") + " 23:59:59",
      timezone,
    ),
    cert: convertTimeToUTC(
      claimDetail.get("cert_due_date") + " 23:59:59",
      timezone,
    ),
    claim_internal_notes: claimDetail.get("claim_internal_notes"),
    claim_external_notes: claimDetail.get("claim_external_notes"),
    less_paid_to_date: roundNearestInt(multiplyValues([lessPaid, 100])),
    less_retention: roundNearestInt(multiplyValues([lessRetention, 100])),
    baseitems: baseList,
    variations: variationList,
    previous_paid_to_date: previousType,
  };
  return (dispatch, getState) => {
    let contractInfo = getState().getIn(["makeaclaim", "contractInfo"]),
      pcdReleaseValue = getState().getIn([
        "makeaclaim",
        "claimPcdReleaseValue",
      ]),
      dlpReleaseValue = getState().getIn([
        "makeaclaim",
        "claimDlpReleaseValue",
      ]),
      certRetention = getState().getIn(["makeaclaim", "certRetention"]),
      toClaimRetention = getState().getIn(["makeaclaim", "toClaimRetention"]),
      materialList = getState().getIn(["makeaclaim", "materialsClaimList"]),
      claimUpdateMessage =
        message ||
        getState().getIn([
          "config",
          "accountConfig",
          "pages",
          "claim",
          "claim_toast_terms",
          "toast_updated",
        ]),
      previous_option = getState().getIn(["makeaclaim", "previous_option"]),
      previous_object_id = getState().getIn([
        "makeaclaim",
        "previous_object_id",
      ]),
      previous_gross = getState().getIn(["makeaclaim", "previous_gross"]),
      previous_retention = getState().getIn([
        "makeaclaim",
        "previous_retention",
      ]),
      previous_retention_pcrr = getState().getIn([
        "makeaclaim",
        "previous_retention_pcrr",
      ]),
      previous_retention_dlrr = getState().getIn([
        "makeaclaim",
        "previous_retention_dlrr",
      ]),
      previous_labour_element = getState().getIn([
        "makeaclaim",
        "previous_labour_element",
      ]),
      labour_element = getState().getIn(["makeaclaim", "labour_element"]);
    const accountId = getState().getIn([
      "headers",
      "profile",
      "payclaim_account_id",
    ]);
    if (claimDetail.get("has_retention")) {
      if (toClaimRetention) {
        dataSet["pcd_release_value"] = floorValue(pcdReleaseValue);
        dataSet["dlp_release_value"] = floorValue(dlpReleaseValue);
      } else {
        dataSet["pcd_release_value"] = floorValue(
          multiplyValues([certRetention.get("pcd"), 100]),
        );
        dataSet["dlp_release_value"] = floorValue(
          multiplyValues([certRetention.get("dlp"), 100]),
        );
      }
      dataSet["pcd_date"] = contractInfo.get("pcd_date");
      dataSet["dlp_date"] = contractInfo.get("dlp_date");
    }
    materialList = materialList.toJS();
    materialList.map((item) => {
      item.percent = parseFloatValue(item.percent);
      item.value = roundNearestInt(item.value);
      item.claim_qty = roundNumber(
        itemQuantity(item?.value, item?.total, item?.total_item_quantity),
      );
      return item;
    });
    dataSet["materials"] = materialList;
    dataSet["claim_less_previous_option"] = lessPreviousOption;
    dataSet["previous_option"] = previous_option;
    dataSet["previous_gross"] = previous_gross;
    dataSet["previous_retention"] = previous_retention;
    dataSet["previous_retention_pcrr"] = previous_retention_pcrr;
    dataSet["previous_retention_dlrr"] = previous_retention_dlrr;
    dataSet["previous_object_id"] = previous_object_id;
    dataSet["previous_labour_element"] = previous_labour_element;
    dataSet["labour_element"] = labour_element;
    API.update_one_claim(proId, contractId, claimId, JSON.stringify(dataSet))
      .then((res) => res.data)
      .then((res) => {
        let nextPath = `/project/${proId}/contract/${contractId}/${contractType}/claim/${claimId}/review?account_id=${accountId}`;
        if (field === "claim") {
          history.push(nextPath);
        } else {
          switch (showType) {
            case "modal":
              dispatch(
                actionCreatorsModal.showModal("confirm", {
                  open: true,
                  title: "Success!",
                  message: claimUpdateMessage,
                  action: requestContractInfo(
                    proId,
                    contractId,
                    claimId,
                    contractType,
                  ),
                }),
              );
              break;
            case "toast":
              dispatch(
                actionCreatorsToast.addToast(claimId, {
                  type: "information",
                  title: claimUpdateMessage,
                }),
              );
              break;
          }
        }
      });
  };
};

// get previous claim and cert data
export const getPreviousClaimCert = (
  proId,
  contractId,
  claimId,
  contractType,
  claim_to,
) => {
  return (dispatch) => {
    API.read_contract_summary(proId, contractId)
      .then((res) => res.data.data.contractsummary)
      .then((res) => {
        dispatch(setPreviousClaimCert(res, claimId, contractType, claim_to));
      });
  };
};

// set previous claim/cert data in redux store
const setPreviousClaimCert = (data, claimId, contractType, claim_to) => ({
  type: constants.SET_PREVIOUS_CLAIM_CERT,
  currentClaimID: claimId,
  data: data,
  contractType: contractType,
  claim_to: claim_to,
});

export const setPreviousOption = (previousOption = "") => ({
  type: constants.SET_PREVIOUS_OPTION,
  option: previousOption,
});

export const updateClaimLineItem = (
  proId,
  contractId,
  lineId,
  value,
  callback,
) => {
  value.quantity = roundNearestInt(multiplyValues([value.quantity, 100]));
  value.rate = roundNearestInt(multiplyValues([value.rate, 100]));
  if (value.submit_date && value.submit_date.indexOf("T") !== -1) {
    value.submit_date = value.submit_date.split("T")[0];
  }
  if (value.approved_date && value.approved_date.indexOf("T") !== -1) {
    value.approved_date = value.approved_date.split("T")[0];
  }

  //add the id of lineitem so that api server knows it's an update
  value["id"] = lineId;
  let data = new FormData();
  data.append("data", JSON.stringify(value));

  return (dispatch) => {
    API.upsert_one_lineitem(
      proId,
      contractId,
      data,
      "variations",
      {
        mimeType: "multipart/form-data",
      },
      true,
    )
      .then((res) => res.data)
      .then(() => {
        API.read_contract_lineitem(proId, contractId, lineId)
          .then((res) => res.data)
          .then((res) => {
            dispatch(setVariationLineItemInRedux(res?.data?.data, "EDIT"));
          });
      });
  };
};

export const createNewClaimRow = (
  proId,
  contractId,
  claimId,
  tableList,
  files,
  table,
  lineitemOption,
  callback,
) => {
  tableList["quantity"] = roundNearestInt(
    multiplyValues([tableList.quantity, 100]),
  );
  tableList["rate"] = roundNearestInt(multiplyValues([tableList.rate, 100]));
  tableList["lineitem_options"] = lineitemOption;
  let itemData = tableList;
  itemData = JSON.stringify(itemData);
  var dataSend = new FormData();
  files.map((item) => {
    return dataSend.append("attachments", item);
  });
  dataSend.append("data", itemData);
  return (dispatch) => {
    API.create_one_claim_lineitem(
      proId,
      contractId,
      claimId,
      {
        mimeType: "multipart/form-data",
      },
      dataSend,
    )
      .then((res) => res.data)
      .then((res) => {
        // Add New Line Item at the end of the Variation
        dispatch(setVariationLineItemInRedux(res?.data?.data, "ADD"));
      })
      .then(() => {
        callback?.();
      });
  };
};

//add child line item to the project and clean the input
export const createNewClaimChildRow = (
  proId,
  contractId,
  claimId,
  tableList,
  files,
  parentItemId,
  table,
  lineitemOption,
  variationType,
  callback,
) => {
  tableList["quantity"] = roundNearestInt(
    multiplyValues([tableList.quantity, 100]),
  );
  tableList["rate"] = roundNearestInt(multiplyValues([tableList.rate, 100]));
  tableList["lineitem_options"] = lineitemOption;
  tableList.parent_id = parentItemId;
  tableList.extra_info.variation_type = variationType;

  let itemData = tableList;
  itemData = JSON.stringify(itemData);
  var dataSend = new FormData();
  files.map((item) => {
    return dataSend.append("attachments", item);
  });
  dataSend.append("data", itemData);
  return (dispatch) => {
    API.create_one_claim_lineitem(
      proId,
      contractId,
      claimId,
      {
        mimeType: "multipart/form-data",
      },
      dataSend,
    )
      .then((res) => res.data)
      .then((res) => {
        dispatch(setVariationLineItemInRedux(res?.data?.data, "ADD"));
        callback?.();
      });
  };
};

export const deleteLineItem = (
  proId,
  contractId,
  claimId,
  itemId,
  callback,
) => {
  return (dispatch) => {
    API.delete_one_claim_lineitem(proId, contractId, claimId, itemId)
      .then((res) => res.data)
      .then((res) => {
        callback?.();
        dispatch(setVariationLineItemInRedux(res?.data?.data, "DELETE"));
      });
  };
};

export const createXeroInvoice = async (proId, contractId, claimId) => {
  return API.create_xero_invoice(proId, contractId, claimId)
    .then((res) => res.data.data.data)
    .then((res) => {
      return res;
    });
};

export const handleTreeExpand = (key, appendKey) => ({
  type: constants.HANDLE_LINE_TREE_EXPAND,
  key,
  appendKey,
});

export const deleteAttachedEmailFile = (fileID) => ({
  type: constants.DELETE_ATTACHED_EMAIL_FILE,
  payload: fileID,
});

export const addAttachedEmailFile = (file) => ({
  type: constants.ADD_ATTACHED_EMAIL_FILE,
  payload: file,
});
